class DrawerMenu extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this;
    this.mainLevel = this.el.querySelector('.drawer-menu__contents');
    this.separatedPanelAnimationFrames = new Map();
    this.pendingThemeOpenTimeout = null;
    this.pendingThemeOpenHandler = null;
    this.init();
  }

  init() {
    // These toggle submenu panel (side panel drilldown)
    this.firstLevelItemsWithChildren = this.el.querySelectorAll('.drawer-menu__item__input');
    this.firstLevelItemsWithChildren.forEach((input) => {
      input.addEventListener('change', this.toggleMenuItem.bind(this));
    });

    // These toggle inline accordion (expand/collapse in place)
    this.inlineToggles = this.el.querySelectorAll('.drawer-menu__inline-toggle');
    this.inlineToggles.forEach((input) => {
      input.addEventListener('change', this.toggleInlineMenu.bind(this));
    });

    // These handle closing of navigation
    this.closeButtons = this.el.querySelectorAll('[data-close]');
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', this.closeAllItems.bind(this));
    });

    this.overlay = this.el.querySelector('[data-overlay]');
    this.overlay.addEventListener('click', this.closeAllItems.bind(this));

    // These handle going back to previous panel
    this.secondLevelBackButtons = this.el.querySelectorAll('[data-drawer-submenu-close]');
    this.secondLevelBackButtons.forEach((button) => {
      button.addEventListener('click', this.toggleSubPanel.bind(this));
    });

    // Handle opening of specific subitem from anywhere in the theme
    this.subitemOpeners = document.querySelectorAll('[data-drawer-submenu-open]');
    this.subitemOpeners.forEach((button) => {
      button.addEventListener('click', this.toggleMenuItemFromTheme.bind(this));
    });

    // Keep menu state in sync when the drawer is closed externally.
    this.drawerClassObserver = new MutationObserver(() => {
      if (!this.el.classList.contains('is-open')) {
        this.clearPendingThemeOpen();
        this.closeAllItems();
      }
    });
    this.drawerClassObserver.observe(this.el, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Find the separated panel container for an inline child input.
   * These panels are rendered outside the inline parent in the DOM
   * but linked via data-inline-child-panel attribute.
   */
  getSeparatedPanel(inputId) {
    return this.el.querySelector(`[data-inline-child-panel="${inputId}"]`);
  }

  /**
   * Show/hide a separated inline child panel.
   * Mirrors the behavior of a normal accordion item's panel visibility.
   */
  toggleSeparatedPanel(inputId, show) {
    const panel = this.getSeparatedPanel(inputId);
    if (!panel) return;

    if (this.separatedPanelAnimationFrames.has(inputId)) {
      cancelAnimationFrame(this.separatedPanelAnimationFrames.get(inputId));
      this.separatedPanelAnimationFrames.delete(inputId);
    }

    if (show) {
      panel.style.maxHeight = '100dvh';
      panel.style.visibility = 'visible';
      // Use requestAnimationFrame to ensure the panel is visible before
      // triggering the transition, so the slide animation plays correctly
      const animationFrameId = requestAnimationFrame(() => {
        const input = this.el.querySelector(`[id="${inputId}"]`);
        if (!input || !input.checked) {
          this.separatedPanelAnimationFrames.delete(inputId);
          return;
        }
        panel.classList.add('is-open');
        this.separatedPanelAnimationFrames.delete(inputId);
      });
      this.separatedPanelAnimationFrames.set(inputId, animationFrameId);
    } else {
      panel.classList.remove('is-open');
      panel.style.maxHeight = '';
      panel.style.visibility = '';
    }
  }

  closeAllItems(event, skipEl) {
    const checkboxes = this.mainLevel.querySelectorAll('.drawer-menu__item__input:checked');
    checkboxes.forEach((checkbox) => {
      if (skipEl && checkbox === skipEl) {
        return;
      }
      checkbox.checked = false;

      // Hide separated panel if this is an inline child
      this.toggleSeparatedPanel(checkbox.id, false);

      const accordion = checkbox.closest('[data-accordion-item]');
      if (accordion) {
        accordion.classList.remove('is-open');
        if (screen.width <= 820) {
          const parent = accordion.closest('.drawer-menu__contents');
          parent.classList.remove('pull-left');
        }
      }
    });

    // Close any stale separated panels that may still be open due to queued animation callbacks.
    const openSeparatedPanels = this.el.querySelectorAll('[data-inline-child-panel].is-open');
    openSeparatedPanels.forEach((panel) => {
      const inputId = panel.getAttribute('data-inline-child-panel');
      if (!inputId) return;
      if (skipEl && inputId === skipEl.id) return;
      this.toggleSeparatedPanel(inputId, false);
    });

    // Also remove inline submenu state
    const inlineParents = this.mainLevel.querySelectorAll('.--inline-menu');
    inlineParents.forEach((parent) => {
      parent.classList.remove('has-open-inline-submenu');
    });

    if (!skipEl) {
      this.mainLevel.classList.remove('has-open-submenu');
    }
  }

  toggleInlineMenu(scope) {
    const input = scope.currentTarget || scope;
    if (!input) return;

    // Close any open side panels (both regular and inline children)
    this.closeAllItems(scope, null);

    const accordion = input.closest('[data-accordion-item]');
    if (!accordion) return;

    accordion.classList.toggle('is-open', input.checked);
  }

  toggleMenuItem(scope) {
    const input = scope.currentTarget || scope;
    if (!input) return;

    // Always close all other open panels (both regular and inline)
    this.closeAllItems(scope, input);

    const accordion = input.closest('[data-accordion-item]');
    if (!accordion) {
      return;
    }
    if (screen.width <= 820) {
      const parent = accordion.closest('.drawer-menu__contents');
      parent.classList.toggle('pull-left', input.checked);
    }

    const isOpen = input.checked;
    accordion.classList.toggle('is-open', isOpen);

    // Show/hide separated panel for inline children
    this.toggleSeparatedPanel(input.id, isOpen);

    // Check if this input is inside an inline menu
    const inlineParent = input.closest('.--inline-menu');

    if (isOpen) {
      this.mainLevel.classList.add('has-open-submenu');
      if (inlineParent) {
        inlineParent.classList.add('has-open-inline-submenu');
      }
    } else {
      this.mainLevel.classList.remove('has-open-submenu');
      if (inlineParent) {
        inlineParent.classList.remove('has-open-inline-submenu');
      }
    }
  }

  clearPendingThemeOpen() {
    if (this.pendingThemeOpenHandler) {
      this.el.removeEventListener('transitionend', this.pendingThemeOpenHandler);
      this.pendingThemeOpenHandler = null;
    }

    if (this.pendingThemeOpenTimeout) {
      window.clearTimeout(this.pendingThemeOpenTimeout);
      this.pendingThemeOpenTimeout = null;
    }
  }

  resetFromDefaultCheckedState() {
    this.clearPendingThemeOpen();
    this.closeAllItems();

    const inputs = this.el.querySelectorAll('.drawer-menu__item__input, .drawer-menu__inline-toggle');
    inputs.forEach((input) => {
      input.checked = input.defaultChecked;
    });

    // Re-apply open state only for inputs that are default checked.
    inputs.forEach((input) => {
      if (input.checked) {
        input.dispatchEvent(new Event('change'));
      }
    });
  }

  toggleMenuItemFromTheme(scope) {
    let target = false;
    const button = scope.currentTarget || scope;
    const url = button.getAttribute('data-drawer-submenu-open');

    // Search in regular drilldown inputs
    for (const input of this.firstLevelItemsWithChildren) {
      if (input.getAttribute('data-href') === url) {
        target = input;
        break;
      }
    }

    // If not found, search in inline toggles
    if (!target) {
      for (const input of this.inlineToggles) {
        if (input.getAttribute('data-href') === url) {
          target = input;
          break;
        }
      }
    }

    if (!target) return;

    this.clearPendingThemeOpen();

    const openTarget = () => {
      if (!this.el.classList.contains('is-open')) return;
      const evt = new Event('change');
      target.checked = true;
      target.dispatchEvent(evt);
    };

    if (this.el.classList.contains('is-open')) {
      openTarget();
      return;
    }

    Woolman.ModalsAndDrawers.showModalOrDrawer(this.el.getAttribute('id'));

    this.pendingThemeOpenHandler = (event) => {
      if (event.target !== this.el) return;
      this.clearPendingThemeOpen();
      openTarget();
    };
    this.el.addEventListener('transitionend', this.pendingThemeOpenHandler);

    // Fallback in case transitionend doesn't fire for this element.
    this.pendingThemeOpenTimeout = window.setTimeout(() => {
      this.clearPendingThemeOpen();
      openTarget();
    }, 300);
  }

  disconnectedCallback() {
    this.drawerClassObserver.disconnect();
    this.clearPendingThemeOpen();

    for (const frameId of this.separatedPanelAnimationFrames.values()) {
      cancelAnimationFrame(frameId);
    }
    this.separatedPanelAnimationFrames.clear();
  }

  toggleSubPanel(event) {
    this.closeAllItems();

    const button = event.currentTarget;
    const targetSelector = button.getAttribute('data-drawer-submenu-close');
    if (!targetSelector) {
      return;
    }

    const input = document.getElementById(targetSelector);
    if (!input) {
      return;
    }

    input.focus();
  }
}

if (customElements.get('drawer-menu') === undefined) {
  customElements.define('drawer-menu', DrawerMenu);
}

// Reset drawer menu item inputs
window.addEventListener('pageshow', (event) => {
  // Only reset on bfcache restores to avoid clobbering live interactions during initial load.
  if (!event.persisted) return;

  document.querySelectorAll('drawer-menu').forEach((drawerMenu) => {
    if (typeof drawerMenu.resetFromDefaultCheckedState === 'function') {
      drawerMenu.resetFromDefaultCheckedState();
    }
  });
});